/**
 * About Page - Founder info, mission, and team
 * Builds trust with transparency
 */
import { motion } from 'framer-motion';
import { User, Heart, Target, Code, Zap, Mail, MapPin, Calendar } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';

export default function About() {
  const navigate = useNavigate();

  const team = [
    {
      name: "Tommy",
      role: "Chief Mascot Officer",
      description: "The cyberpunk tuxedo cat who represents TripleTC's spirit - independent, resourceful, and a little rebellious.",
      image: "https://images.unsplash.com/photo-1592194996308-7b43878e84a6?w=200&h=200&fit=crop",
      isAI: false
    },
    {
      name: "Daisy",
      role: "AI Assistant",
      description: "Your friendly guide through TripleTC. She helps with onboarding, answers questions, and makes AI feel approachable.",
      image: "https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=200&h=200&fit=crop",
      isAI: true
    },
    {
      name: "Helios",
      role: "Research Analyst",
      description: "The analytical mind behind our research tools. Data-driven, thorough, and always digging deeper.",
      image: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=200&h=200&fit=crop",
      isAI: true
    }
  ];

  const timeline = [
    { year: "2025", event: "Idea born - frustrated with expensive, fragmented AI tools" },
    { year: "Early 2026", event: "Development begins - nights and weekends" },
    { year: "March 2026", event: "TripleTC launches with 50+ templates" },
    { year: "April 2026", event: "Video generation, custom chatbots, voiceover added" },
    { year: "Now", event: "Building the future of accessible AI tools" }
  ];

  return (
    <div className="pt-24 pb-16 min-h-screen" data-testid="about-page">
      <div className="max-w-5xl mx-auto px-6">
        
        {/* Hero Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-16"
        >
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Built by One Person.<br />
            <span className="text-teal-400">For Everyone.</span>
          </h1>
          <p className="text-xl text-zinc-400 max-w-3xl mx-auto">
            TripleTC isn't backed by venture capital or built by a faceless corporation. 
            It's an indie project created to make AI tools accessible to entrepreneurs, 
            freelancers, and anyone trying to build something.
          </p>
        </motion.div>

        {/* Founder Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-gradient-to-br from-teal-500/10 to-cyan-500/10 border border-teal-500/30 rounded-2xl p-8 mb-16"
        >
          <div className="flex flex-col md:flex-row items-center gap-8">
            <div className="w-32 h-32 rounded-full bg-gradient-to-br from-teal-500 to-cyan-500 flex items-center justify-center flex-shrink-0">
              <User className="w-16 h-16 text-white" />
            </div>
            <div className="text-center md:text-left">
              <h2 className="text-2xl font-bold text-white mb-2">Jedadiah Spruill</h2>
              <p className="text-teal-400 mb-4">Founder & Developer</p>
              <p className="text-zinc-300 mb-4">
                I built TripleTC because I was tired of seeing AI tools that were either 
                too expensive, too complicated, or designed to extract maximum money for 
                minimum value. I wanted to create something different - a platform where 
                you pay for results, not subscriptions. Where small business owners, 
                freelancers, and job seekers could access the same AI capabilities as 
                big companies.
              </p>
              <p className="text-zinc-400 text-sm">
                8 months of development. No VC funding. No paid actors in testimonials. 
                Just real tools for real people.
              </p>
              <div className="flex flex-wrap items-center gap-4 mt-4 justify-center md:justify-start">
                <span className="flex items-center gap-2 text-zinc-500 text-sm">
                  <MapPin className="w-4 h-4" /> Kasilof, Alaska
                </span>
                <span className="flex items-center gap-2 text-zinc-500 text-sm">
                  <Calendar className="w-4 h-4" /> Building since 2025
                </span>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Mission Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="mb-16"
        >
          <h2 className="text-2xl font-bold text-white mb-8 text-center">Why TripleTC Exists</h2>
          
          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6 text-center">
              <div className="w-12 h-12 bg-red-500/20 rounded-xl flex items-center justify-center mx-auto mb-4">
                <Heart className="w-6 h-6 text-red-400" />
              </div>
              <h3 className="text-white font-semibold mb-2">The Problem</h3>
              <p className="text-zinc-400 text-sm">
                AI tools are either expensive subscriptions, confusing to use, or filled 
                with hollow promises. Small businesses get left behind.
              </p>
            </div>
            
            <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6 text-center">
              <div className="w-12 h-12 bg-teal-500/20 rounded-xl flex items-center justify-center mx-auto mb-4">
                <Target className="w-6 h-6 text-teal-400" />
              </div>
              <h3 className="text-white font-semibold mb-2">The Solution</h3>
              <p className="text-zinc-400 text-sm">
                One platform with 50+ AI tools. Pay only for what you use. No subscriptions 
                required. Real results for real businesses.
              </p>
            </div>
            
            <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6 text-center">
              <div className="w-12 h-12 bg-purple-500/20 rounded-xl flex items-center justify-center mx-auto mb-4">
                <Zap className="w-6 h-6 text-purple-400" />
              </div>
              <h3 className="text-white font-semibold mb-2">The Mission</h3>
              <p className="text-zinc-400 text-sm">
                Make AI accessible to everyone - not just big companies with big budgets. 
                Level the playing field.
              </p>
            </div>
          </div>
        </motion.div>

        {/* Timeline */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="mb-16"
        >
          <h2 className="text-2xl font-bold text-white mb-8 text-center">Our Journey</h2>
          
          <div className="relative">
            <div className="absolute left-1/2 transform -translate-x-1/2 h-full w-0.5 bg-zinc-800" />
            
            {timeline.map((item, index) => (
              <div key={index} className={`flex items-center gap-4 mb-8 ${index % 2 === 0 ? 'flex-row' : 'flex-row-reverse'}`}>
                <div className={`w-1/2 ${index % 2 === 0 ? 'text-right pr-8' : 'text-left pl-8'}`}>
                  <span className="text-teal-400 font-semibold">{item.year}</span>
                  <p className="text-zinc-300 text-sm">{item.event}</p>
                </div>
                <div className="w-4 h-4 bg-teal-500 rounded-full z-10 flex-shrink-0" />
                <div className="w-1/2" />
              </div>
            ))}
          </div>
        </motion.div>

        {/* Team Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="mb-16"
        >
          <h2 className="text-2xl font-bold text-white mb-8 text-center">Meet the Team</h2>
          
          <div className="grid md:grid-cols-3 gap-6">
            {team.map((member, index) => (
              <motion.div
                key={member.name}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 + index * 0.1 }}
                className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6 text-center hover:border-teal-500/30 transition-colors"
              >
                <img 
                  src={member.image} 
                  alt={member.name}
                  className="w-24 h-24 rounded-full mx-auto mb-4 object-cover border-2 border-zinc-700"
                />
                <h3 className="text-white font-semibold">{member.name}</h3>
                <p className="text-teal-400 text-sm mb-2">{member.role}</p>
                {member.isAI && (
                  <span className="inline-block bg-purple-500/20 text-purple-400 text-xs px-2 py-0.5 rounded mb-2">
                    AI Character
                  </span>
                )}
                <p className="text-zinc-400 text-sm">{member.description}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Values */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="bg-zinc-900/50 border border-zinc-800 rounded-2xl p-8 mb-16"
        >
          <h2 className="text-2xl font-bold text-white mb-6 text-center">What We Believe</h2>
          
          <div className="grid md:grid-cols-2 gap-6">
            <div className="flex gap-4">
              <div className="w-2 bg-teal-500 rounded-full flex-shrink-0" />
              <div>
                <h3 className="text-white font-semibold mb-1">Transparency Over Hype</h3>
                <p className="text-zinc-400 text-sm">No paid actors. No fake testimonials. What you see is what you get.</p>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="w-2 bg-cyan-500 rounded-full flex-shrink-0" />
              <div>
                <h3 className="text-white font-semibold mb-1">Results Over Subscriptions</h3>
                <p className="text-zinc-400 text-sm">Pay for what you use, not for access you don't need.</p>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="w-2 bg-purple-500 rounded-full flex-shrink-0" />
              <div>
                <h3 className="text-white font-semibold mb-1">Quality Over Quantity</h3>
                <p className="text-zinc-400 text-sm">Every template is tested. Every feature has a purpose.</p>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="w-2 bg-pink-500 rounded-full flex-shrink-0" />
              <div>
                <h3 className="text-white font-semibold mb-1">Community Over Competition</h3>
                <p className="text-zinc-400 text-sm">We're here to help indie builders succeed, not crush them.</p>
              </div>
            </div>
          </div>
        </motion.div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7 }}
          className="text-center"
        >
          <h2 className="text-2xl font-bold text-white mb-4">Ready to Get Started?</h2>
          <p className="text-zinc-400 mb-6">
            Join thousands of entrepreneurs, freelancers, and creators using TripleTC.
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <Button 
              onClick={() => navigate('/templates')}
              className="bg-teal-500 hover:bg-teal-600"
            >
              Try Free Templates
            </Button>
            <Button 
              onClick={() => navigate('/contact')}
              variant="outline"
              className="border-zinc-700 text-zinc-300 hover:bg-zinc-800"
            >
              <Mail className="w-4 h-4 mr-2" />
              Contact Us
            </Button>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
